import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import { Images } from "@assets/index";

export default function Navbar() {
  const { t } = useTranslation("landing");
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToSection = (id: string) => {
    setOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const links = [
    { label: t("navbar.home"), id: "home" },
    { label: t("navbar.program"), id: "program" },
    { label: t("navbar.themes"), id: "themes" },
    { label: t("navbar.journey"), id: "journey" },
  ];

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-slate-200 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:h-20 lg:px-8">
        <button onClick={() => scrollToSection("home")} className="flex items-center gap-3 bg-transparent border-0 cursor-pointer p-0 text-left">
          {/* Conditional filter transforms the white logo to dark when scrolled */}
          <img
            src={Images.tpaLogo}
            alt="The Purpose Academy"
            className={`h-10 w-auto object-contain lg:h-12 transition-all duration-300 ${
              scrolled ? "invert brightness-90 hue-rotate-[220deg]" : "invert-0 brightness-100"
            }`}
          />
          <span className={`hidden h-8 w-px sm:inline-block transition-colors ${scrolled ? 'bg-slate-300' : 'bg-white/10'}`} />
          <span className={`hidden text-[13px] font-extrabold tracking-[0.18em] sm:inline uppercase transition-colors ${scrolled ? 'text-[#0D0820]' : 'text-white'}`}>
            AI IMPACT <span className="bg-gradient-to-r from-[oklch(0.55_0.24_295)] via-[oklch(0.62_0.27_0)] to-[oklch(0.72_0.22_35)] bg-clip-text text-transparent">FELLOWSHIP</span>
          </span>
        </button>

        <nav className="hidden items-center gap-8 lg:flex">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollToSection(l.id)}
              className={`text-sm font-medium transition-colors bg-transparent border-0 cursor-pointer ${
                scrolled ? 'text-slate-600 hover:text-navy' : 'text-slate-300 hover:text-white'
              }`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <button
            onClick={() => scrollToSection("apply")}
            className="rounded-full btn-cinematic px-5 py-2.5 text-sm font-semibold transition-transform hover:-translate-y-0.5 cursor-pointer border-0 text-white"
          >
            {t("navbar.applyNow")}
          </button>
        </div>

        <button
          aria-label="Menu"
          className={`grid h-10 w-10 place-items-center rounded-lg border lg:hidden cursor-pointer transition-colors ${
            scrolled ? 'border-slate-200 bg-transparent text-navy' : 'border-white/10 bg-transparent text-white'
          }`}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className={`border-t lg:hidden ${scrolled ? 'border-slate-200 bg-white' : 'border-white/5 bg-[#0D0820]'}`}>
          <div className="space-y-1 px-5 py-4 flex flex-col items-stretch">
            {links.map((l) => (
              <button
                key={l.id}
                onClick={() => scrollToSection(l.id)}
                className={`text-left block rounded-lg px-3 py-2.5 text-base font-medium bg-transparent border-0 cursor-pointer ${
                  scrolled ? 'text-slate-600 hover:bg-slate-50 hover:text-navy' : 'text-slate-300 hover:bg-white/5 hover:text-white'
                }`}
              >
                {l.label}
              </button>
            ))}
            <div className="flex gap-2 pt-2">
              <button
                onClick={() => scrollToSection("apply")}
                className={`flex-1 rounded-full border py-2.5 text-center text-sm font-semibold cursor-pointer ${
                  scrolled ? 'border-slate-300 bg-transparent text-slate-700' : 'border-white/10 bg-transparent text-slate-300'
                }`}
              >
                {t("navbar.talkToUs")}
              </button>
              <button
                onClick={() => scrollToSection("apply")}
                className="flex-1 rounded-full bg-primary py-2.5 text-center text-sm font-semibold text-white cursor-pointer border-0"
              >
                {t("navbar.applyNow")}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}